import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretLeft, CaretRight, MapPin, Buildings } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ProviderCard from "../components/ProviderCard";
import { api } from "../lib/api";
import { breadcrumbSchema, richItemListSchema } from "../lib/jsonLd";

const US_STATES = {
  "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
  "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "FL": "Florida", "GA": "Georgia",
  "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois", "IN": "Indiana", "IA": "Iowa",
  "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
  "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi", "MO": "Missouri",
  "MT": "Montana", "NE": "Nebraska", "NV": "Nevada", "NH": "New Hampshire", "NJ": "New Jersey",
  "NM": "New Mexico", "NY": "New York", "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio",
  "OK": "Oklahoma", "OR": "Oregon", "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina",
  "SD": "South Dakota", "TN": "Tennessee", "TX": "Texas", "UT": "Utah", "VT": "Vermont",
  "VA": "Virginia", "WA": "Washington", "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming",
  "DC": "District of Columbia"
};

const POPULAR_SPECIALTIES = [
  "Cardiology",
  "Dermatology",
  "Pediatrics",
  "Psychiatry",
  "Orthopedics",
  "Family Medicine",
  "Internal Medicine",
  "Neurology"
];

const ITEMS_PER_PAGE = 20;

const CityDirectory = () => {
  const { stateCode, city } = useParams();
  
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const stateUpper = stateCode.toUpperCase();
  const stateName = US_STATES[stateUpper] || stateCode;
  const cityName = city.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        state: stateUpper,
        city: cityName,
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE
      };
      if (specialtyFilter && specialtyFilter !== "all") {
        params.taxonomy_description = specialtyFilter;
      }
      
      const data = await api.searchProviders(params);
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
    } catch (error) {
      console.error("Error fetching city providers:", error);
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [stateUpper, cityName, specialtyFilter, currentPage]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleSpecialtyChange = (value) => {
    setSpecialtyFilter(value);
    setCurrentPage(1);
  };

  const pageTitle = "Doctors in " + cityName + ", " + stateUpper + " - Find Healthcare Providers | NPI Lookup";
  const pageDescription = "Find " + totalCount.toLocaleString() + "+ healthcare providers in " + cityName + ", " + stateName + ". Browse verified NPI numbers, credentials, and contact information for doctors in " + cityName + ".";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={"https://npilookup.com/state/" + stateCode.toLowerCase() + "/" + city.toLowerCase()} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "States", url: "/states" },
          { name: stateName, url: "/state/" + stateCode.toLowerCase() },
          { name: cityName },
        ]))}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: pageTitle,
          description: pageDescription,
          url: "https://npilookup.com/state/" + stateCode.toLowerCase() + "/" + city.toLowerCase(),
          about: {
            "@type": "City",
            name: cityName,
            containedInPlace: { "@type": "State", name: stateName },
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify(richItemListSchema(
          "Healthcare Providers in " + cityName + ", " + stateUpper,
          pageDescription,
          results,
          "/state/" + stateCode.toLowerCase() + "/" + city.toLowerCase(),
        ))}</script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm">
            <ol className="flex items-center gap-2 text-slate-500">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              <li><Link to={"/state/" + stateCode} className="hover:text-teal">{stateName}</Link></li>
              <li>/</li>
              <li className="text-slate-900 font-medium">{cityName}</li>
            </ol>
          </nav>

          {/* Header */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center">
                <Buildings className="w-6 h-6 text-navy" />
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span className="text-slate-500 font-medium">{stateName}</span>
              </div>
            </div>
            
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-navy mb-2" data-testid="city-title">
              Healthcare Providers in {cityName}, {stateUpper}
            </h1>
            <p className="text-slate-500 text-lg">
              Browse all registered healthcare providers in {cityName}, {stateName}
            </p>
            
            {/* Specialty Filter */}
            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <label className="text-sm font-medium text-slate-700">Filter by specialty:</label>
              <Select value={specialtyFilter} onValueChange={handleSpecialtyChange}>
                <SelectTrigger className="w-full sm:w-64" data-testid="city-specialty-filter">
                  <SelectValue placeholder="All specialties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All specialties</SelectItem>
                  {POPULAR_SPECIALTIES.map((spec) => (
                    <SelectItem key={spec} value={spec}>
                      {spec}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <span className="text-sm text-slate-500" data-testid="city-result-count">
                {loading ? "Loading..." : totalCount.toLocaleString() + " providers found"}
              </span>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="skeleton h-4 w-20 mb-3 rounded" />
                  <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/3 mb-4 rounded" />
                  <div className="skeleton h-10 w-full rounded" />
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                No providers found
              </h3>
              <p className="text-slate-500 mb-6">
                Try selecting a different specialty or browse all providers in {stateName}.
              </p>
              <Link to={"/state/" + stateCode}>
                <Button variant="outline">
                  View All Providers in {stateName}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="city-results-grid"
              >
                {results.map((provider) => (
                  <ProviderCard key={provider.number} provider={provider} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <CaretLeft className="w-4 h-4" />
                  </Button>
                  
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={"pagination-btn " + (currentPage === pageNum ? 'active bg-navy' : '')}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    <CaretRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
          
          {/* SEO Content */}
          <div className="mt-12 bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="font-display font-semibold text-xl text-slate-900 mb-4">
              Find Healthcare Providers in {cityName}, {stateName}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our directory includes {totalCount.toLocaleString()}+ registered healthcare providers in {cityName}, {stateName}. 
              Each provider listing includes verified NPI numbers, credentials, contact information, 
              and practice addresses. Use the NPI Lookup to verify provider credentials and find 
              doctors near you in {cityName}.
            </p>
            
            {/* Popular Specialties in this city */}
            <h3 className="font-display font-semibold text-lg text-slate-900 mt-6 mb-3">
              Popular Specialties in {cityName}
            </h3>
            <div className="flex flex-wrap gap-2">
              {POPULAR_SPECIALTIES.map((spec) => (
                <button
                  key={spec}
                  onClick={() => handleSpecialtyChange(spec)}
                  className="text-sm px-3 py-1.5 bg-slate-100 rounded-lg hover:bg-teal/10 hover:text-teal text-slate-600 transition-colors"
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CityDirectory;
